import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { WarehouseDetailHook } from '../../../hooks';
import { AddWarehousePopup, WarehouseDetailTab } from '../../../page-components';
import { GenerateLocationLabelPopup } from '../../../page-components/warehouse/GenerateLocationPopup';
import { SettingsWarehouseToolbar } from '../../../page-components/settings/settings-warehouse-toolbar';
import { CommonUtility } from '../../../utility';
import { WarehouseZonesHook } from '../../../hooks/zone';
import { ZoneTab } from './zones';

const WarehouseContext = createContext();

export function WarehouseTab({ warehouseId, onCreateWarehouse }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [generateLabelOpen, setGenerateLabelOpen] = useState(false);

  const openPopup = () => {
    setOpen(true);
  };

  const closePopup = () => {
    setOpen(false);
  };

  const { data, loading, refresh: refreshWarehouse } = WarehouseDetailHook(warehouseId);

  const {
    data: zones,
    loading: zoneLoading,
    refresh: zoneRefresh,
  } = WarehouseZonesHook(warehouseId);

  const zoneOptions = useMemo(() => {
    if (CommonUtility.isValidArray(zones)) {
      return zones?.map(wh => ({
        text: wh?.name,
        key: wh?._id,
        value: wh?._id,
      }));
    }
    return [];
  }, [zones]);

  const zoneId = useMemo(() => {
    if (pathname) {
      return CommonUtility.getSegmentByPosition(pathname, 5);
    }
    return null;
  }, [pathname]);

  const inToZone = useMemo(() => {
    if (pathname) {
      return pathname.includes('zones');
    }
    return false;
  }, [pathname]);

  const onChangeZone = zoneId => {
    navigate(`/app/settings/warehouse/zones/${zoneId}`);
  };

  useEffect(() => {
    if (CommonUtility.isValidArray(zoneOptions) && inToZone && warehouseId) {
      const isInOptions = zoneOptions.find(x => x.value === zoneId);
      if (!isInOptions) {
        onChangeZone(zoneOptions[0]?.value);
      }
    }
  }, [zoneOptions, zoneId, inToZone, warehouseId]);

  return (
    <WarehouseContext.Provider
      value={{
        warehouseData: data,
        warehouseLoading: loading,
        refreshWarehouse,
        warehouseId,
      }}
    >
      {!inToZone ? (
        <SettingsWarehouseToolbar
          onAddWarehouse={openPopup}
          onGenerateLabel={() => setGenerateLabelOpen(true)}
        />
      ) : null}
      <Routes>
        <Route
          index
          element={
            <WarehouseDetailTab
              zones={zones}
              zoneLoading={zoneLoading}
              zoneRefresh={zoneRefresh}
            />
          }
        />
        <Route
          path="zones/:id/*"
          element={
            <ZoneTab
              warehouseId={warehouseId}
              onChangeZone={onChangeZone}
              zoneId={zoneId}
              zoneOptions={zoneOptions}
            />
          }
        />
      </Routes>
      <AddWarehousePopup
        onCreateWarehouse={onCreateWarehouse}
        open={open}
        onClose={closePopup}
      />
      {!inToZone ? (
        <GenerateLocationLabelPopup
          open={generateLabelOpen}
          onClose={() => setGenerateLabelOpen(false)}
          warehouseId={warehouseId}
        />
      ) : null}
    </WarehouseContext.Provider>
  );
}

export const useWarehouseDetails = () => useContext(WarehouseContext);
